import React, { useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft } from "react-feather";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  X,
  Upload,
  FilePlus,
  Package,
  ClipboardCheck,
  ChevronRight,
  Plus,
  Trash2,
} from "lucide-react";
import DatePicker, { CalendarContainer } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { displayToast } from "../../utils/Toast";
import { useErrorHandler } from "../../services/ErrorHandler";
import Edit from "../../assets/svgIcons/FormEdit.svg";
import Delete from "../../assets/svgIcons/FormDelete.svg";
import ConfirmationModal from "../../components/ConfirmationModal";
import Loader from "../../components/Loader";
import { capitalizeFirstLetterOfEachWord } from "../../utils/Formatter";
import { getFieldMaster } from "../../apis/fieldMaster";
import {
  insertMasterTransaction,
  fetchTransactionById,
  editMasterTransaction,
  insertProductMaster,
  editProductMaster,
  fetchTransactionPopupData,
  deleteTransactionByCode,
  fetchVendorCategoriesList,
  fetchSubcategoriesByCategoryId,
  fetchAllVendorSubcategories,
  deleteCategory,
  deleteSubcategory,
} from "../../apis/transaction";
import { useMode } from "../../context/ModeContext";
import { useSalesFormStoreApi } from "../../services/useSalesFormStore";
import { usePermissions } from "../../hooks/usePermissions";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import CategoryManagementModal from "../../components/CategoryManagementModal";
import SubcategoryManagementModal from "../../components/SubcategoryManagementModal";
import "../../styles/components/MasterDataModal.css";

const DateCalendar = ({ children, onClear, className, ...props }) => {
  const [dropdownRow, setDropdownRow] = useState(null);
  const calendarRef = useCallback((node) => {
    setDropdownRow(
      node?.querySelector(".react-datepicker__header__dropdown") ?? null,
    );
  }, []);

  return (
    <div ref={calendarRef}>
      <CalendarContainer
        {...props}
        className={`${className || ""} clearable-datepicker-calendar`}
      >
        {children}
      </CalendarContainer>
      {dropdownRow &&
        onClear &&
        createPortal(
          <button
            type="button"
            className="react-datepicker__month-read-view calendar-clear-button"
            onClick={onClear}
          >
            Clear
          </button>,
          dropdownRow,
        )}
    </div>
  );
};

const VendorMasterDataModal = ({
  transactionCode: propTransactionCode,
  heading = "",
  selectedDetail = "",
  modeOfEdit = "",
  onClose,
  extraForms,
  newEntryTranCode,
  customerType = "",
  isPopupMode = false,
}) => {
  const location = useLocation();
  const salesFormStore = useSalesFormStoreApi();
  const { setMode } = useMode();
  const { tab, category, subType, mode } = useParams();
  const navigate = useNavigate();
  const { handleErrorResponse } = useErrorHandler();

  const transactionCode = (() => {
    if (propTransactionCode) {
      return propTransactionCode;
    }
    if (location.state?.transactionCode) {
      return location.state.transactionCode;
    }
    if (heading === "otherDetailsModal") {
      return location.state?.transactionCode;
    }
    return null;
  })();

  const isSupplier =
    transactionCode === "VM" &&
    String(subType || "").toLowerCase() === "suppliers";

  const displayCategory =
    transactionCode === "CM"
      ? "customer"
      : transactionCode === "VM"
        ? isSupplier
          ? "supplier"
          : "vendor"
        : transactionCode === "PM"
          ? "new Product"
          : category;

  const transId = location.state?.transId;
  const colorCode = location.state?.colorCode;
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [currentMode, setCurrentMode] = useState(mode);
  useAutoRefresh(() => fetchTransactionData(), {
    enabled: currentMode === "view" && Boolean(transId),
  });
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [openDropdownField, setOpenDropdownField] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const rights = usePermissions(transactionCode);
  const inputRefs = useRef({});
  const categoryListRef = useRef(null);
  const [vendorCategories, setVendorCategories] = useState([]);
  const [categorySubcategories, setCategorySubcategories] = useState({});
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [subcategoryLoading, setSubcategoryLoading] = useState({});
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryModalMode, setCategoryModalMode] = useState("add");
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState(null);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [subcategoryModalMode, setSubcategoryModalMode] = useState("add");
  const [selectedSubcategoryForEdit, setSelectedSubcategoryForEdit] =
    useState(null);
  const [deletionConfirmation, setDeletionConfirmation] = useState({
    isOpen: false,
    itemId: null,
    itemType: null,
    itemName: "",
  });

  const visibleSelectedSubcategories = Array.isArray(selectedSubcategories)
    ? selectedSubcategories.filter((sub) => sub && sub.id != null)
    : [];

  const sanitizeVendorHeadData = (data) => {
    if (transactionCode !== "VM") return data || {};
    const sanitized = { ...(data || {}) };
    delete sanitized.sub_category_ids;
    return sanitized;
  };

  const detailButtons =
    extraForms?.length > 0
      ? extraForms.map((form) =>
          capitalizeFirstLetterOfEachWord(form.label || form.title || ""),
        )
      : [];

  useEffect(() => {
    if (heading === "otherDetailsModal" && Array.isArray(selectedDetail)) {
      const { otherDetailsData } = salesFormStore.getState();

      const initialData = selectedDetail.reduce((acc, field) => {
        const existingData = otherDetailsData.find(
          (detail) => detail[field.field_name] !== undefined,
        );
        acc[field.field_name] = existingData
          ? existingData[field.field_name]
          : "";
        return acc;
      }, {});

      setFormData(initialData);
    }
  }, [selectedDetail, heading, salesFormStore]);

  useEffect(() => {
    if (currentMode === "add") {
      setMode("add");
    } else if (currentMode === "edit") {
      setMode("edit");
    } else {
      setMode("view");
    }
  }, [setMode, currentMode]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".modal-dropdown-container")) {
        setOpenDropdownField(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleCategoryOutsideClick = (e) => {
      if (
        expandedCategory === null ||
        showSubcategoryModal ||
        showCategoryModal ||
        showDeleteConfirmation ||
        deletionConfirmation.isOpen
      ) {
        return;
      }

      if (
        categoryListRef.current &&
        !categoryListRef.current.contains(e.target)
      ) {
        setExpandedCategory(null);
      }
    };

    document.addEventListener("mousedown", handleCategoryOutsideClick);
    return () =>
      document.removeEventListener("mousedown", handleCategoryOutsideClick);
  }, [
    expandedCategory,
    showSubcategoryModal,
    showCategoryModal,
    showDeleteConfirmation,
    deletionConfirmation.isOpen,
  ]);

  useEffect(() => {
    const initializeData = async () => {
      if (heading === "otherDetailsModal") {
        setFields(selectedDetail);
      } else {
        await fetchFieldMaster();
        if (mode === "view" && transId) {
          await fetchTransactionData();
        }
      }
    };
    initializeData();
  }, [transactionCode, transId, heading]);

  useEffect(() => {
    if (!isSupplier) return;
    const loadCategories = async () => {
      setCategoryLoading(true);
      try {
        const response = await fetchVendorCategoriesList();
        const items =
          response?.items ||
          response?.data ||
          (Array.isArray(response) ? response : []);
        setVendorCategories(items);
      } catch (error) {
        displayToast("Failed to fetch categories", "error");
      } finally {
        setCategoryLoading(false);
      }
    };
    loadCategories();
  }, [transactionCode, isSupplier]);

  useEffect(() => {
    if (!isSupplier) return;
    if (!transactionData) return;

    const subCategoryIds = transactionData?.sub_category_ids;

    if (!Array.isArray(subCategoryIds) || subCategoryIds.length === 0) {
      setSelectedSubcategories([]);
      return;
    }

    const prePopulate = async () => {
      try {
        const response = await fetchAllVendorSubcategories();

        const allSubs =
          response?.items || (Array.isArray(response) ? response : []);

        if (!Array.isArray(allSubs) || allSubs.length === 0) {
          setSelectedSubcategories([]);
          return;
        }

        const preSelected = allSubs
          .filter((sub) => subCategoryIds.includes(sub.id))
          .map((sub) => ({
            id: sub.id,
            name: sub.subcategory_name || sub.name || "",
            categoryId: sub.category_id || sub.categoryId || "",
            categoryName: sub.category_name || sub.categoryName || "",
          }));

        setSelectedSubcategories(preSelected);
      } catch (error) {
        setSelectedSubcategories([]);
      }
    };

    prePopulate();
  }, [transactionData, transactionCode, isSupplier]);

  const fetchTransactionData = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await fetchTransactionById(transactionCode, transId);

      const subCategoryIds = response?.head_data?.sub_category_ids;

      if (response.description) {
        displayToast(response.description, "error");
      } else {
        const enhancedResponse = {
          ...response,
          sub_category_ids: subCategoryIds,
        };
        setTransactionData(enhancedResponse);
        if (response.head_data) {
          setFormData(sanitizeVendorHeadData(response.head_data));
        }
      }
    } catch (error) {
      displayToast("Failed to fetch transaction data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFieldMaster = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await getFieldMaster({
        transactionCode: transactionCode,
      });
      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        let validFields = (response.items?.head_fields || [])
          .filter((field) => !field.is_internal_field)
          .sort((a, b) => a.order_no - b.order_no);

        if (isPopupMode) {
          validFields = validFields.filter((field) => field.is_required);
        }

        setFields(validFields);

        if (mode !== "view") {
          const initialFormData = validFields.reduce((acc, field) => {
            acc[field.field_name] = "";
            return acc;
          }, {});

          setFormData(initialFormData);
        }
      }
    } catch (error) {
      displayToast("Failed to fetch field master data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (value === "Add New +") {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryToggle = async (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
      return;
    }
    setExpandedCategory(categoryId);
    if (!categorySubcategories[categoryId]) {
      setSubcategoryLoading((prev) => ({ ...prev, [categoryId]: true }));
      try {
        const response = await fetchSubcategoriesByCategoryId(categoryId);
        const items =
          response?.items ||
          response?.data ||
          (Array.isArray(response) ? response : []);
        setCategorySubcategories((prev) => ({ ...prev, [categoryId]: items }));
      } catch (error) {
        displayToast("Failed to fetch subcategories", "error");
      } finally {
        setSubcategoryLoading((prev) => ({ ...prev, [categoryId]: false }));
      }
    }
  };

  const handleSubcategoryToggle = (sub, cat) => {
    const isSelected = selectedSubcategories.some((s) => s.id === sub.id);
    if (isSelected) {
      setSelectedSubcategories((prev) => prev.filter((s) => s.id !== sub.id));
    } else {
      setSelectedSubcategories((prev) => [
        ...prev,
        {
          id: sub.id,
          name: sub.subcategory_name || "",
          categoryId: cat.id,
          categoryName: cat.category_name || "",
        },
      ]);
    }
  };

  const handleRemoveSubcategory = (subId) => {
    setSelectedSubcategories((prev) => prev.filter((s) => s.id !== subId));
  };

  const openAddCategoryModal = () => {
    setCategoryModalMode("add");
    setSelectedCategoryForEdit(null);
    setShowCategoryModal(true);
  };

  const openEditCategoryModal = (category) => {
    setCategoryModalMode("edit");
    setSelectedCategoryForEdit(category);
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (category) => {
    const isCategoryUsed = selectedSubcategories.some(
      (sub) => sub.categoryId === category.id,
    );

    if (isCategoryUsed) {
      displayToast(
        "Cannot delete this category as it is mapped to vendors",
        "error",
      );
      return;
    }

    setDeletionConfirmation({
      isOpen: true,
      itemId: category.id,
      itemType: "category",
      itemName: category.category_name,
    });
  };

  const confirmDeleteCategory = async () => {
    const { itemId } = deletionConfirmation;
    try {
      const response = await deleteCategory(itemId);
      if (response && response.status === "success") {
        displayToast("Category deleted successfully", "success");

        const loadResponse = await fetchVendorCategoriesList();
        const items =
          loadResponse?.items ||
          loadResponse?.data ||
          (Array.isArray(loadResponse) ? loadResponse : []);
        setVendorCategories(items);
        setDeletionConfirmation({
          isOpen: false,
          itemId: null,
          itemType: null,
          itemName: "",
        });
      } else {
        displayToast(response?.message || "Failed to delete category", "error");
      }
    } catch (error) {
      displayToast(
        "Error deleting category: " + (error.message || "Unknown error"),
        "error",
      );
    }
  };

  const openAddSubcategoryModal = (categoryId) => {
    setSubcategoryModalMode("add");
    setSelectedSubcategoryForEdit(null);
    setExpandedCategory(categoryId);
    setShowSubcategoryModal(true);
  };

  const openEditSubcategoryModal = (subcategory) => {
    setSubcategoryModalMode("edit");
    setSelectedSubcategoryForEdit(subcategory);
    setExpandedCategory(
      subcategory?.category_id || subcategory?.categoryId || expandedCategory,
    );
    setShowSubcategoryModal(true);
  };

  const handleDeleteSubcategory = async (subcategory) => {
    const isSubcategoryUsed = selectedSubcategories.some(
      (sub) => sub.id === subcategory.id,
    );

    if (isSubcategoryUsed) {
      displayToast(
        "Cannot delete this subcategory as it is mapped to vendors",
        "error",
      );
      return;
    }

    setDeletionConfirmation({
      isOpen: true,
      itemId: subcategory.id,
      itemType: "subcategory",
      itemName: subcategory.subcategory_name,
    });
  };

  const confirmDeleteSubcategory = async () => {
    const { itemId } = deletionConfirmation;
    try {
      const response = await deleteSubcategory(itemId);
      if (response && response.status === "success") {
        displayToast("Subcategory deleted successfully", "success");
        if (expandedCategory) {
          setSubcategoryLoading((prev) => ({
            ...prev,
            [expandedCategory]: true,
          }));
          const loadResponse =
            await fetchSubcategoriesByCategoryId(expandedCategory);
          const items =
            loadResponse?.items ||
            loadResponse?.data ||
            (Array.isArray(loadResponse) ? loadResponse : []);
          setCategorySubcategories((prev) => ({
            ...prev,
            [expandedCategory]: items,
          }));
          setSubcategoryLoading((prev) => ({
            ...prev,
            [expandedCategory]: false,
          }));
        }
        setDeletionConfirmation({
          isOpen: false,
          itemId: null,
          itemType: null,
          itemName: "",
        });
      } else {
        displayToast(
          response?.message || "Failed to delete subcategory",
          "error",
        );
      }
    } catch (error) {
      displayToast(
        "Error deleting subcategory: " + (error.message || "Unknown error"),
        "error",
      );
    }
  };

  const refreshCategoryAndSubcategoryData = async (
    updatedCategoryId = null,
    keepExpanded = true,
  ) => {
    const targetCategoryId = updatedCategoryId || expandedCategory;
    setCategoryLoading(true);
    try {
      const response = await fetchVendorCategoriesList();
      const items =
        response?.items ||
        response?.data ||
        (Array.isArray(response) ? response : []);
      setVendorCategories(items);

      if (targetCategoryId) {
        if (keepExpanded) {
          setExpandedCategory(targetCategoryId);
        }
        setSubcategoryLoading((prev) => ({
          ...prev,
          [targetCategoryId]: true,
        }));
        try {
          const subResponse =
            await fetchSubcategoriesByCategoryId(targetCategoryId);
          const subItems =
            subResponse?.items ||
            subResponse?.data ||
            (Array.isArray(subResponse) ? subResponse : []);
          setCategorySubcategories((prev) => ({
            ...prev,
            [targetCategoryId]: subItems,
          }));
        } finally {
          setSubcategoryLoading((prev) => ({
            ...prev,
            [targetCategoryId]: false,
          }));
        }
      }
    } catch (error) {
      displayToast("Failed to refresh categories", "error");
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleCategoryModalSuccess = async (updatedCategoryId = null) => {
    setShowCategoryModal(false);
    await refreshCategoryAndSubcategoryData(updatedCategoryId, true);
  };

  const handleSubcategoryModalSuccess = async (updatedCategoryId = null) => {
    setShowSubcategoryModal(false);
    await refreshCategoryAndSubcategoryData(updatedCategoryId, false);
    setExpandedCategory(null);
  };

  const handleSubcategoryModalClose = () => {
    setShowSubcategoryModal(false);
    setExpandedCategory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsButtonLoading(true);

    if ((currentMode || modeOfEdit) === "add" && !rights.can_add) {
      displayToast(
        `You dont have permission to add ${displayCategory}`,
        "error",
      );
      setIsButtonLoading(false);
      return;
    }

    if (currentMode === "edit" && !rights.can_edit) {
      displayToast(
        `You dont have permission to edit ${displayCategory}`,
        "error",
      );
      setIsButtonLoading(false);
      return;
    }

    const { addOtherDetails, otherDetailsData } = salesFormStore.getState();

    if (heading === "otherDetailsModal" && modeOfEdit === "add") {
      let detailsMap = new Map();

      otherDetailsData.forEach((item) => {
        const key = Object.keys(item)[0];
        detailsMap.set(key, item);
      });

      Object.keys(formData).forEach((key) => {
        detailsMap.set(key, { [key]: formData[key] });
      });

      const updatedDetails = Array.from(detailsMap.values());

      addOtherDetails(updatedDetails);
      displayToast("Other details saved successfully", "success");
      onClose();
      setIsButtonLoading(false);
      return;
    }

    const isAddMode = (currentMode || modeOfEdit) === "add";
    const safeHeadData = sanitizeVendorHeadData(formData);

    if (isAddMode) {
      const organisation = JSON.parse(localStorage.getItem("organisation"));
      const organisationId = organisation?.id;
      if (!organisationId) {
        displayToast("Organisation ID not found", "error");
        setIsButtonLoading(false);
        return;
      }

      let submissionData = {
        head_data: {
          organisation_id: organisationId,
          tran_no: "",
          ...safeHeadData,
          ...otherDetailsData,
        },
      };

      if (isSupplier) {
        submissionData = {
          head_data: {
            organisation_id: organisationId,
            tran_no: "",
            ...safeHeadData,
            ...otherDetailsData,
          },
          sub_category_ids: selectedSubcategories.map((s) => s.id),
        };
      }

      try {
        let response;

        if (transactionCode === "PM") {
          response = await insertProductMaster(submissionData);
        } else {
          response = await insertMasterTransaction(
            submissionData,
            transactionCode,
          );
        }

        if (response?.head_data) {
          displayToast("Transaction added successfully", "success");

          if (modeOfEdit !== "add") {
            if (category) {
              sessionStorage.setItem(`clearCache_master_${category}`, "true");
            }
            navigate(-1);
          } else {
            navigate(`/${tab}/${category}/transactions`, {
              state: {
                company: response.head_data,
                transactionCode: newEntryTranCode,
                customerType: customerType,
              },
            });
            onClose();
          }
        } else if (response?.description) {
          handleErrorResponse(response.description);
          displayToast(response.description, "error");
        }
      } catch (error) {
        displayToast("Error adding transaction", "error");
      } finally {
        setIsButtonLoading(false);
      }
    } else if (currentMode === "edit" && transId) {
      try {
        let updatedData = {
          tran_id: transId,
          head_data: {
            ...safeHeadData,
          },
          item_data: [],
        };

        if (isSupplier) {
          updatedData = {
            tran_id: transId,
            head_data: {
              ...safeHeadData,
            },
            item_data: [],
            sub_category_ids: selectedSubcategories.map((s) => s.id),
          };
        }

        let response;

        if (transactionCode === "PM") {
          response = await editProductMaster(updatedData);
        } else {
          response = await editMasterTransaction(transactionCode, updatedData);
        }

        if (response?.head_data) {
          displayToast("Transaction updated successfully", "success");
          if (category) {
            sessionStorage.setItem(`clearCache_master_${category}`, "true");
          }
          navigate(-1);
        } else if (response.description) {
          handleErrorResponse(response.description);
          displayToast(response.description, "error");
        }
      } catch (error) {
        displayToast("Error updating transaction", "error");
      } finally {
        setIsButtonLoading(false);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const deleteTranId = transId || transactionData?.tran_id;
      const deleteCode = transactionCode === "VM" ? "VM" : transactionCode;

      if (!deleteTranId || !deleteCode) {
        displayToast(
          "Unable to delete vendor: missing transaction details",
          "error",
        );
        return;
      }

      const response = await deleteTransactionByCode(deleteCode, deleteTranId);

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        displayToast("Transaction deleted successfully", "success");
        if (category) {
          sessionStorage.setItem(`clearCache_master_${category}`, "true");
        }
        navigate(-1);
      }
    } catch (error) {
      displayToast("Error deleting transaction", "error");
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  const handleDeleteClick = () => {
    if (!rights.can_delete) {
      displayToast(
        `You don't have permission to delete ${displayCategory}`,
        "error",
      );
      return;
    }
    setShowDeleteConfirmation(true);
  };

  const toggleEditMode = () => {
    if (!rights.can_edit) {
      displayToast(
        `You dont have permission to edit ${displayCategory}`,
        "error",
      );
      return;
    }
    setCurrentMode("edit");
  };

  const handleCloseEditMode = () => {
    setCurrentMode("view");
    if (mode === "view" && transId) {
      setSelectedSubcategories([]);
      fetchTransactionData();
    }
  };

  useEffect(() => {
    const fetchAllDropdownOptions = async () => {
      const queryFields = fields.filter(
        (field) =>
          field.data_type === "DROPDOWN" && field.is_query && field.db_query,
      );

      const newOptions = {};

      for (const field of queryFields) {
        try {
          const response = await fetchTransactionPopupData(field.db_query);
          if (response?.items?.length > 0) {
            newOptions[field.field_name] = response.items?.map((item) =>
              typeof item === "object" ? item.category || "" : item,
            );
          } else {
            displayToast(response.message, "error");
          }
        } catch (error) {
          displayToast(
            `Failed to fetch options for ${field.title || field.field_name}`,
            "error",
          );
        }
      }

      setDropdownOptions((prev) => ({ ...prev, ...newOptions }));
    };

    fetchAllDropdownOptions();
  }, [fields]);

  const renderField = (field) => {
    if (!field) return null;

    const fieldValue = formData[field.field_name] || "";

    const optionsToRender = field.is_query
      ? dropdownOptions[field.field_name] || []
      : field.options
          ?.split(",")
          .map((opt) => opt.trim())
          .map(
            (opt) => opt.charAt(0).toUpperCase() + opt.slice(1).toLowerCase(),
          );

    if (field.field_type === "DATE") {
      return (
        <div className="master-data-modal-form-field" key={field.field_name}>
          <label className="master-data-modal-label">
            {field.title}
            {field.is_required && <span className="required-mark">*</span>}
          </label>
          <DatePicker
            selected={fieldValue ? new Date(fieldValue) : null}
            onChange={(date) =>
              setFormData((prev) => ({
                ...prev,
                [field.field_name]: date,
              }))
            }
            dateFormat="dd-MM-yyyy"
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={26}
            dropdownMode="scroll"
            calendarContainer={(props) => (
              <DateCalendar
                {...props}
                onClear={
                  currentMode === "view"
                    ? undefined
                    : () =>
                        setFormData((prev) => ({
                          ...prev,
                          [field.field_name]: null,
                        }))
                }
              />
            )}
            className="master-data-modal-input"
            placeholderText={`Select ${field.title.toLowerCase()}`}
            disabled={currentMode === "view"}
            wrapperClassName="master-data-modal-datepicker-wrapper"
            popperProps={{ strategy: "fixed" }}
            portalId="root-portal"
          />
        </div>
      );
    }
    const isMultiLineField =
      field.field_type?.toUpperCase() === "TEXT" ||
      (field.field_length &&
        field.field_length > 60 &&
        !field.options &&
        !field.is_query);

    if (isMultiLineField) {
      const adjustHeight = (textarea) => {
        if (textarea) {
          textarea.style.height = "24px";
          const newHeight = Math.min(textarea.scrollHeight, 72);
          textarea.style.height = `${newHeight}px`;
          textarea.style.overflowY =
            textarea.scrollHeight > 72 ? "auto" : "hidden";
        }
      };

      return (
        <div className="master-data-modal-form-field" key={field.field_name}>
          <label className="master-data-modal-label">
            {field.title}
            {field.is_required && <span className="required-mark">*</span>}
          </label>
          <textarea
            name={field.field_name}
            value={fieldValue || ""}
            onChange={(e) => {
              handleInputChange(e);
              if (currentMode !== "view") adjustHeight(e.target);
            }}
            className={`master-type-text-input ${
              currentMode === "view" ? "non-editable" : ""
            }`}
            disabled={currentMode === "view"}
            placeholder={`Enter ${field.title || field.field_name}`}
            ref={(el) => {
              inputRefs.current[field.field_name] = { current: el };
              if (el) adjustHeight(el);
            }}
            rows={1}
            maxLength={field.field_length}
            required={field.is_required}
          />
        </div>
      );
    }
    return (
      <div className="master-data-modal-form-field" key={field.field_name}>
        <label className="master-data-modal-label">
          {field.title}
          {field.is_required && <span className="required-mark">*</span>}
        </label>
        {field.options ? (
          <div className="modal-dropdown-container">
            <div
              className={`customer-data-modal-input ${
                currentMode === "view"
                  ? "non-editable"
                  : "master-data-modal-select-wrapper"
              } ${
                openDropdownField === field.field_name
                  ? "dropdown-open-highlight"
                  : ""
              }`}
              onClick={() =>
                currentMode !== "view" && setOpenDropdownField(field.field_name)
              }
            >
              <span className="modal-dropdown-text">
                {fieldValue || `Select ${field.title}`}
              </span>
              <span className="dropdown-icon">▼</span>
            </div>

            {openDropdownField === field.field_name &&
              currentMode !== "view" && (
                <div className="primary-dropdown-options">
                  {Array.isArray(optionsToRender) &&
                    optionsToRender.map((option, index) => {
                      const isSelected = formData[field.field_name] === option;
                      return (
                        <div
                          key={index}
                          className={`primary-dropdown-option ${
                            isSelected ? "selected-option" : ""
                          }`}
                          onClick={() => {
                            handleInputChange({
                              target: {
                                name: field.field_name,
                                value: option,
                              },
                            });
                            setOpenDropdownField(null);
                          }}
                        >
                          {option}
                        </div>
                      );
                    })}
                </div>
              )}
          </div>
        ) : field.field_type === "BOOLEAN" ? (
          <input
            type="checkbox"
            name={field.field_name}
            className="master-data-modal-checkbox"
            checked={!!fieldValue}
            onChange={(e) =>
              handleInputChange({
                target: {
                  name: field.field_name,
                  value: e.target.checked,
                },
              })
            }
            disabled={currentMode === "view"}
          />
        ) : (
          <input
            type={field.data_type === "int" ? "number" : "text"}
            name={field.field_name}
            className={`master-data-modal-input ${
              currentMode === "view" ? "non-editable" : ""
            }`}
            placeholder={`Enter ${field.title.toLowerCase()}`}
            value={fieldValue}
            onChange={handleInputChange}
            readOnly={currentMode === "view"}
            maxLength={field.field_length}
            required={field.is_required}
          />
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="master-data-modal-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="master-data-modal-container">
      <div
        className="master-data-modal-header"
        style={{
          backgroundColor: colorCode ? `#${colorCode}` : "#fff",
        }}
      >
        <div className="master-data-modal-title-container">
          <h2 className="master-data-modal-title">
            {heading !== "otherDetailsModal"
              ? `${
                  isPopupMode ? "Quick Add " : ""
                }${capitalizeFirstLetterOfEachWord(
                  currentMode,
                )} ${capitalizeFirstLetterOfEachWord(displayCategory)}`
              : `${capitalizeFirstLetterOfEachWord(
                  modeOfEdit,
                )} ${capitalizeFirstLetterOfEachWord(selectedDetail.title)}`}
          </h2>
          {currentMode === "view" ? (
            <>
              <img
                src={Edit}
                onClick={toggleEditMode}
                className="sales-form-icons"
                alt="Edit"
                style={{
                  cursor: rights.can_edit ? "pointer" : "not-allowed",
                  opacity: rights.can_edit ? 1 : 0.5,
                }}
                title={rights.can_edit ? "Edit" : "No permission to edit"}
              />
              <img
                src={Delete}
                className="sales-form-icons"
                alt="Delete"
                onClick={handleDeleteClick}
                style={{
                  cursor: rights.can_delete ? "pointer" : "not-allowed",
                  opacity: rights.can_delete ? 1 : 0.5,
                  marginLeft: 12,
                }}
                title={
                  rights.can_delete
                    ? "Delete Transaction"
                    : "No permission to delete"
                }
              />
              {window.location.pathname.startsWith("/master") && (
                <button className="back-button-modal" onClick={onClose}>
                  <ArrowLeft size={25} />
                </button>
              )}
            </>
          ) : (window.location.pathname.startsWith("/master") &&
              currentMode === "add") ||
            currentMode !== "add" ? (
            <X
              className="sales-form-icons cursor-pointer"
              onClick={() =>
                modeOfEdit === "add" ||
                (window.location.pathname.startsWith("/master") &&
                  currentMode === "add")
                  ? onClose()
                  : handleCloseEditMode()
              }
              size={24}
              title="Close Mode"
            />
          ) : null}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="master-data-modal-form">
        <div
          className={
            heading !== "otherDetailsModal"
              ? "master-data-modal-form-fields"
              : "master-data-modal-field"
          }
        >
          {(() => {
            const normalFields = fields.filter(
              (f) => f.field_type !== "TEXT" && f.field_type !== "text",
            );
            const textFields = fields.filter(
              (f) => f.field_type === "TEXT" || f.field_type === "text",
            );
            return [...normalFields, ...textFields].map((field) =>
              renderField(field),
            );
          })()}
        </div>

        {isSupplier && heading !== "otherDetailsModal" && (
          <div className="vm-category-section">
            <div className="vm-category-section-header">
              <h6 className="vm-category-section-title">
                Categories &amp; Subcategories
              </h6>
              {currentMode !== "view" && (
                <button
                  type="button"
                  className="vm-add-category-button"
                  onClick={openAddCategoryModal}
                  title="Add Category"
                >
                  <Plus size={18} /> Add Category
                </button>
              )}
            </div>

            {/* Selected subcategory tags */}
            <div className="vm-selected-tags">
              {visibleSelectedSubcategories.length === 0 ? (
                <span className="vm-selected-tags-placeholder">
                  No subcategories selected
                </span>
              ) : (
                visibleSelectedSubcategories.map((sub, index) => (
                  <span key={`${sub.id}-${index}`} className="vm-tag">
                    <span>
                      {sub.categoryName ? `${sub.categoryName} › ` : ""}
                      {sub.name}
                    </span>
                    {currentMode !== "view" && (
                      <button
                        type="button"
                        className="vm-tag-remove"
                        onClick={() => handleRemoveSubcategory(sub.id)}
                        title="Remove"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </span>
                ))
              )}
            </div>

            {currentMode !== "view" &&
              (categoryLoading ? (
                <div className="vm-category-loading">Loading categories…</div>
              ) : (
                <div className="vm-category-list" ref={categoryListRef}>
                  {vendorCategories.length === 0 ? (
                    <div className="vm-category-loading">
                      No categories available
                    </div>
                  ) : (
                    vendorCategories.map((cat) => (
                      <div key={cat.id} className="vm-category-item">
                        <div
                          className={`vm-category-header-wrapper ${
                            expandedCategory === cat.id ? "expanded" : ""
                          }`}
                        >
                          <div
                            className="vm-category-header"
                            onClick={() => handleCategoryToggle(cat.id)}
                          >
                            <span className="vm-category-name">
                              {cat.category_name}
                            </span>
                            <ChevronRight
                              size={16}
                              className={`vm-category-arrow ${
                                expandedCategory === cat.id ? "expanded" : ""
                              }`}
                            />
                          </div>
                          <div className="vm-category-actions">
                            <button
                              type="button"
                              className="vm-category-edit-button"
                              onClick={() => openEditCategoryModal(cat)}
                              title="Edit Category"
                            >
                              <img
                                src={Edit}
                                alt="Edit"
                                style={{ width: "14px", height: "14px" }}
                              />
                            </button>
                            <button
                              type="button"
                              className="vm-category-delete-button"
                              onClick={() => handleDeleteCategory(cat)}
                              title="Delete Category"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {expandedCategory === cat.id && (
                          <div className="vm-subcategory-list">
                            <div className="vm-subcategory-header">
                              <span>Subcategories</span>
                              <button
                                type="button"
                                className="vm-add-subcategory-button"
                                onClick={() => openAddSubcategoryModal(cat.id)}
                                title="Add Subcategory"
                              >
                                <Plus size={14} /> Add
                              </button>
                            </div>
                            {subcategoryLoading[cat.id] ? (
                              <span className="vm-subcategory-loading">
                                Loading…
                              </span>
                            ) : (categorySubcategories[cat.id] || []).length ===
                              0 ? (
                              <span className="vm-subcategory-loading">
                                No subcategories found
                              </span>
                            ) : (
                              (categorySubcategories[cat.id] || []).map(
                                (sub) => (
                                  <div
                                    key={sub.id}
                                    className="vm-subcategory-item-wrapper"
                                  >
                                    <label className="vm-subcategory-item">
                                      <input
                                        type="checkbox"
                                        checked={selectedSubcategories.some(
                                          (s) => s.id === sub.id,
                                        )}
                                        onChange={() =>
                                          handleSubcategoryToggle(sub, cat)
                                        }
                                      />
                                      {sub.subcategory_name || ""}
                                    </label>
                                    <div className="vm-subcategory-actions">
                                      <button
                                        type="button"
                                        className="vm-subcategory-edit-button"
                                        onClick={() =>
                                          openEditSubcategoryModal(sub)
                                        }
                                        title="Edit Subcategory"
                                      >
                                        <img
                                          src={Edit}
                                          alt="Edit"
                                          style={{
                                            width: "14px",
                                            height: "14px",
                                          }}
                                        />
                                      </button>
                                      <button
                                        type="button"
                                        className="vm-subcategory-delete-button"
                                        onClick={() =>
                                          handleDeleteSubcategory(sub)
                                        }
                                        title="Delete Subcategory"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </div>
                                ),
                              )
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              ))}
          </div>
        )}

        {isPopupMode && (
          <div className="popup-mode-indicator">
            <strong>Quick Add Mode:</strong> Only required fields are shown. For
            complete details, use the master menu.
          </div>
        )}

        {heading !== "otherDetailsModal" && detailButtons.length > 0 && (
          <div className="add-extra-details">
            <h6>Other Details *</h6>
            <div className="add-detail-buttons">
              {detailButtons.map((text, index) => (
                <button key={index} type="button" className="add-detail-button">
                  {text}
                </button>
              ))}
            </div>
          </div>
        )}

        {heading !== "otherDetailsModal" &&
          category === "product-master" &&
          !isPopupMode && (
            <div className="additional-actions master-data-modal-form-fields">
              <div className="action-button-grid">
                <button type="button" className="action-button upload">
                  Upload Drawing
                  <Upload size={18} />
                </button>
                <button
                  type="button"
                  className="action-button bom"
                  onClick={() =>
                    navigate(`/${tab}/${category}/bom`, {
                      state: { productData: formData },
                    })
                  }
                >
                  Create BOM
                  <FilePlus size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (mode !== "add") {
                      navigate(`/${tab}/${category}/packing-list`, {
                        state: { productData: formData },
                      });
                    }
                  }}
                  className="action-button packaging"
                >
                  Add Packaging List
                  <Package size={18} />
                </button>
                <button type="button" className="action-button qc">
                  QC Inspection Format
                  <ClipboardCheck size={18} />
                </button>
              </div>
            </div>
          )}

        <div className="master-data-modal-footer">
          {isButtonLoading ? (
            <div className="loader-container">
              <span className="loader"></span>
            </div>
          ) : (
            (currentMode === "add" ||
              currentMode === "edit" ||
              modeOfEdit === "add") && (
              <div style={{ display: "flex", gap: "1rem" }}>
                <button
                  type="button"
                  className="master-data-modal-cancel-button"
                  onClick={() =>
                    modeOfEdit === "add" ||
                    (window.location.pathname.startsWith("/master") &&
                      currentMode === "add")
                      ? onClose()
                      : handleCloseEditMode()
                  }
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="master-data-modal-save-button"
                  style={{
                    cursor: (
                      currentMode === "add" ? rights.can_add : rights.can_edit
                    )
                      ? "pointer"
                      : "not-allowed",
                    opacity: (
                      currentMode === "add" ? rights.can_add : rights.can_edit
                    )
                      ? 1
                      : 0.5,
                  }}
                >
                  Save
                </button>
              </div>
            )
          )}
        </div>
      </form>
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this transaction?"
        confirmText="Delete"
        cancelText="Cancel"
      />

      <CategoryManagementModal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onSuccess={handleCategoryModalSuccess}
        mode={categoryModalMode}
        categoryData={selectedCategoryForEdit}
      />

      <SubcategoryManagementModal
        isOpen={showSubcategoryModal}
        onClose={handleSubcategoryModalClose}
        onSuccess={handleSubcategoryModalSuccess}
        mode={subcategoryModalMode}
        subcategoryData={selectedSubcategoryForEdit}
        selectedCategoryId={
          selectedSubcategoryForEdit?.category_id ||
          selectedSubcategoryForEdit?.categoryId ||
          expandedCategory
        }
        selectedCategoryName={
          selectedSubcategoryForEdit?.category_name ||
          selectedSubcategoryForEdit?.categoryName ||
          vendorCategories.find((cat) => cat.id === expandedCategory)
            ?.category_name ||
          ""
        }
      />

      <ConfirmationModal
        isOpen={deletionConfirmation.isOpen}
        onClose={() =>
          setDeletionConfirmation({
            isOpen: false,
            itemId: null,
            itemType: null,
            itemName: "",
          })
        }
        onConfirm={
          deletionConfirmation.itemType === "category"
            ? confirmDeleteCategory
            : confirmDeleteSubcategory
        }
        message={`Are you sure you want to delete "${deletionConfirmation.itemName}"?${
          deletionConfirmation.itemType === "category"
            ? " This action cannot be undone."
            : " This action cannot be undone."
        }`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default VendorMasterDataModal;
